/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import {
  $ActivityLogFilters,
  ActivityLog,
  ActivityLogFilters,
  ActivityLogSortableFields,
  GetUserActivityData,
  GetUserActivityResponse,
} from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { usePaginatedQuery } from '@/hooks/query/usePaginatedQuery';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { jsonSchemaToTypeMap } from '@/utils/filters';

export type UsePaginatedUserActivityQueryParams = {
  params?: Omit<GetUserActivityData, 'tenantId'>;
  type?: 'inMemory' | 'queryParams';
};

/**
 * Custom hook that fetches paginated user activity logs with filtering and sorting.
 * Combines React Query for data fetching with WebSocket subscriptions for real-time updates.
 *
 * @param options Configuration object:
 *   - params: Optional query parameters (filters, pagination, sort)
 *   - type: Storage type - 'inMemory' or 'queryParams' for URL persistence
 *
 * @returns Object containing:
 *   - data: Paginated user activity response
 *   - isLoading: Boolean for initial load state
 *   - isFetching: Boolean for any loading state
 *   - isError: Boolean for error state
 *   - error: Error object if request failed
 *   - updateQueryParams: Function to update filters, sort, and pagination
 *
 * Note: tenant filter is automatically applied from the management plane state store
 */
export function usePaginatedUserActivityQuery({ params, type }: UsePaginatedUserActivityQueryParams) {
  const selectedTenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const filters = params?.filters || {};
  // Apply tenant filter automatically
  if (!filters.tenant) {
    filters.tenant = selectedTenantId;
  }

  return usePaginatedQuery<
    GetUserActivityResponse,
    ActivityLog,
    ActivityLogFilters,
    ActivityLogSortableFields,
    GetUserActivityData
  >({
    apiCall: mgmtPlaneAPIClient.activity.getUserActivity.bind(mgmtPlaneAPIClient.activity),
    filterTypes: jsonSchemaToTypeMap($ActivityLogFilters),
    queryKey: 'userActivity',
    realtimeEvents: [],
    params: {
      filters,
      ...params,
    },
    type,
    enabled: !!selectedTenantId,
  });
}
